/**
 * What needs an operator's attention today — one rule, one module.
 *
 * It was two: the register marked rows with its own predicate and the
 * findings panel derived its lines with another. They agreed about most
 * things, which is worse than disagreeing about all of them, because the
 * cases where they parted were invisible. The panel said "an exception
 * has expired, the control reads as failing again" and the row it pointed
 * at sorted last with no marker. Fixing that left the same shape one
 * branch over: a failing control with a live exception and no owner
 * counted in "12 controls have no owner" and was still unmarked.
 *
 * The cure is not a shared boolean — a boolean is what let them drift,
 * because each caller needed slightly different information and grew its
 * own copy. It is a shared list of REASONS. The register marks a row when
 * the list is non-empty; the panel groups the same lists into findings.
 * Neither can hold an opinion the other does not.
 *
 * WHAT COUNTS IS NARROWER THAN "NOT MET". A failing control with an
 * approved, live exception and a compensating measure is a decision
 * somebody already made; surfacing it every morning teaches the operator
 * to scroll past the top of the page, which costs them the one row that
 * did need them.
 */

import type { ControlListItem } from "./schemas";

/** Assertions and decisions inside this window are about to lapse. */
export const EXPIRY_WINDOW_DAYS = 30;

export type AttentionReason =
  | "supporting_failed"
  | "supporting_unavailable"
  /** An approved exception has run out; nothing covers the gap now. */
  | "exception_lapsed"
  /** Failing, and nobody has decided anything about it. */
  | "not_met"
  /** Partly met, and nobody has decided anything about it. */
  | "partly_met"
  /** An assertion or a decision stops being true within the window. */
  | "expiring"
  /** Nothing has produced a verdict. */
  | "unchecked"
  /** Nobody is accountable for it. */
  | "unowned";

/**
 * Every reason this control wants attention. Empty means it does not.
 *
 * Deliberately NOT early-returning: a control can be failing AND unowned,
 * and a rule that stops at the first reason is how the register came to
 * disagree with the panel about rows that had two.
 */
export function attentionReasons(c: ControlListItem): AttentionReason[] {
  // A control ruled outside this organisation's scope has no verdict to
  // want attention about. Both consumers filtered it out separately.
  if (c.treatment === "not_applicable") return [];

  const out: AttentionReason[] = [];
  const excused = c.treatment === "exception";
  const lapsed = excused && isPast(c.exception_expires_at);

  if (lapsed) out.push("exception_lapsed");
  if (c.contribution_status === "non_compliant" || c.contribution_status === "partial") out.push("supporting_failed");
  if (c.contribution_status) {
    const observedAt = Date.parse(c.contribution_observed_at ?? "");
    if (c.contribution_status === "not_assessed" || Number.isNaN(observedAt) || observedAt < Date.now() - 48 * 60 * 60 * 1000) {
      out.push("supporting_unavailable");
    }
  }

  // An excused failure is a decision, unless the excuse has run out.
  if (!excused || lapsed) {
    if (c.status === "non_compliant") out.push("not_met");
    if (c.status === "partial") out.push("partly_met");
  }

  if (c.status === "not_assessed" || c.confidence === "") out.push("unchecked");

  if (
    within(c.attestation_expires_at) ||
    (excused && !lapsed && within(c.exception_expires_at))
  ) {
    out.push("expiring");
  }

  if (c.owner_name === "") out.push("unowned");

  return out;
}

/** Does this row want attention at all? */
export function needsAttention(c: ControlListItem): boolean {
  return attentionReasons(c).length > 0;
}

/** Already past. A lapsed decision is a gap nobody is covering. */
export function isPast(iso: string): boolean {
  if (!iso) return false;
  const at = Date.parse(iso);
  return !Number.isNaN(at) && at < Date.now();
}

/** Inside its last thirty days, and not already gone. */
export function within(iso: string): boolean {
  if (!iso) return false;
  const at = Date.parse(iso);
  if (Number.isNaN(at) || at < Date.now()) return false;
  return at - Date.now() <= EXPIRY_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}
